import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { queryClient } from 'v2/configs/rq-setup'
import type { CommunityVoice, RCommunityVoices } from 'v2/pages/flow/types'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'

export interface CloneVoiceParams {
  label: string
  description?: string
  gender: string
  file: File
}

export interface AddVoiceFromUrlParams {
  voice_url: string
}

export interface CommunityVoicesParams {
  search?: string
  enabled?: boolean
}

export interface CommunityVoicesInfiniteData {
  pages: RCommunityVoices[]
  pageParams: number[]
}

export interface FlattenedCommunityVoices {
  voices: CommunityVoice[]
  hasMore: boolean
}

export const MIN_SEARCH_LENGTH = 3

export const useCloneVoiceMutation = () =>
  useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await flowService.cloneVoice(formData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voices'] })
    },
  })

export const useCommunityVoicesInfiniteQuery = ({
  search,
  enabled = true,
}: CommunityVoicesParams) =>
  useInfiniteQuery<RCommunityVoices, Error, CommunityVoicesInfiniteData, unknown[], number>({
    queryKey: flowQueryKeys().communityVoices(search),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await flowService.getCommunityVoices({
        page: pageParam,
        search,
      })
      return response.data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.has_more) {
        return lastPage.page + 1
      }
      return undefined
    },
    enabled: enabled && !!search && search.trim().length >= MIN_SEARCH_LENGTH,
    staleTime: 30000,
  })
